/**
 * HLOS-16 — PresenceManager (espelha `hlos.kernel.runtime.presence.PresenceEngine`).
 *
 * Port TS literal do contrato Python:
 *   - enum PresenceSignal: APP_FOREGROUND, APP_BACKGROUND, TIMEOUT_5MIN, EXPLICIT_CLOSE
 *   - 3 estados: "active" | "warm" | "cold"
 *   - método `update(signal)` que valida tipo e transiciona
 *   - método `isPresent()` retorna true apenas se state === 'active'
 *
 * Por que espelhar o Python literalmente:
 *   - SL-01 (Scientific Lock): contrato único TS↔Py → mesma semântica
 *   - CL-06 (Iniciativa alterna): R6 ('userInUi=false') → state='cold'
 *     suprime `decideInitiative()` (testado em `manager.spec.ts`)
 *   - Auditabilidade: mesmo nome de signal = mesma origem em ambos runtimes
 *
 * Divergência com `presence/types.ts` (HLOS-2):
 *   - HLOS-2 define `PresenceState = "active" | "idle" | "away" | "dnd"` (4 estados,
 *     vocabulário UX). Mantido para compat com consumers existentes.
 *   - HLOS-16 adiciona `PresenceEngineState` (3 estados) + `PresenceEngineSignal`
 *     (vocabulário Python-like) para o manager. Bridge em `store.ts` converte
 *     entre os dois vocabulários.
 *
 * Referência: docs/decisions/0017-companion-runtime-layer.md §Presence Manager.
 * Constitucional:
 *   - Princípio V (Aprendiz Decide): ausência de UI NUNCA dispara ação autônoma.
 *   - Princípio XI (Companion ≠ LLM): zero chamada externa no fluxo de presença.
 *   - CC-04 (sem exploração de frustração): ausência é só "esperar" — não é
 *     "engagement hack" (não enviamos push notifications em v1, ADR-0011).
 */

export const PresenceEngineSignal = {
  AppForeground: "app_foreground",
  AppBackground: "app_background",
  Timeout5min: "timeout_5min",
  ExplicitClose: "explicit_close",
} as const;

export type PresenceEngineSignal =
  (typeof PresenceEngineSignal)[keyof typeof PresenceEngineSignal];

/** Estado observável da presença (vocabulário Python-like). */
export type PresenceEngineState = "active" | "warm" | "cold";

/** Validador runtime: aceita strings equivalentes ao enum Python. */
function isPresenceEngineSignal(v: unknown): v is PresenceEngineSignal {
  return (
    typeof v === "string" &&
    (Object.values(PresenceEngineSignal) as string[]).includes(v)
  );
}

/**
 * Port TS literal de `hlos.kernel.runtime.presence.PresenceEngine` (Python).
 *
 * Diferenças em relação à store HLOS-2:
 *   - Vocabulário Python (3 estados) vs UI-level (4 estados)
 *   - Validação runtime de `update(signal)` (TypeError se sinal inválido)
 *   - API explícita `isPresent()` em vez de comparar state
 */
export class PresenceManager {
  private _state: PresenceEngineState = "cold";

  constructor(initial: PresenceEngineState = "cold") {
    this._state = initial;
  }

  /** Estado atual observável. */
  get state(): PresenceEngineState {
    return this._state;
  }

  /**
   * Aplica um sinal de presença. Espelha `PresenceEngine.update()` Python.
   *
   * Regras de transição:
   *   - APP_FOREGROUND  → 'active'
   *   - APP_BACKGROUND  → 'warm'
   *   - TIMEOUT_5MIN    → 'cold'
   *   - EXPLICIT_CLOSE  → 'cold'
   *
   * @throws TypeError se `signal` não é um `PresenceEngineSignal` válido.
   */
  update(signal: PresenceEngineSignal): void {
    if (!isPresenceEngineSignal(signal)) {
      throw new TypeError(
        `signal must be a PresenceEngineSignal, got ${typeof signal}: ${String(signal)}`,
      );
    }
    switch (signal) {
      case PresenceEngineSignal.AppForeground:
        this._state = "active";
        break;
      case PresenceEngineSignal.AppBackground:
        this._state = "warm";
        break;
      case PresenceEngineSignal.Timeout5min:
      case PresenceEngineSignal.ExplicitClose:
        this._state = "cold";
        break;
    }
  }

  /**
   * v1 binário: true somente se state === 'active'.
   * v2 (futuro): gradativo — true se state >= 'warm'.
   */
  isPresent(): boolean {
    return this._state === "active";
  }

  /**
   * v1 binário: true se state é exatamente 'cold' (ausente da UI).
   * Usado por R6 para suprimir `decideInitiative()`.
   */
  isAbsent(): boolean {
    return this._state === "cold";
  }
}

/** Helper: cria um manager a partir de um sinal UI-level (HLOS-2). */
export function mapUiSignalToEngine(
  uiSignal: "keystroke" | "pointer_move" | "tab_focus" | "tab_blur" | "user_request",
  nextState: "active" | "idle" | "away" | "dnd",
): PresenceEngineSignal {
  // HLOS-2: uiSignal + nextState são ambos UI-level. Convertemos para o
  // vocabulário engine. Regras (conservadoras — v2 terá mais grão):
  //   - active → AppForeground
  //   - idle/away/dnd → AppBackground
  //
  // O `uiSignal` é anotado para auditoria (qual evento UI causou o sinal).
  // Em runtime, ele NÃO muda o mapeamento — apenas o `nextState` é usado.
  // Mantido na assinatura para extensibilidade futura (v2 pode usar o tipo
  // de sinal para distinguir 'idle by tab_blur' vs 'idle by inactivity').
  void uiSignal;
  if (nextState === "active") {
    return PresenceEngineSignal.AppForeground;
  }
  return PresenceEngineSignal.AppBackground;
}
